/**
 * @file CartLookFields.tsx
 * @description Группы панели «Вид корзины» (phase19.16, партия SB120, этап 4;
 * uiux-cart §4–§5, §7; SB122 — группы-карточки, память
 * `commora.settings-groups.cart`):
 *
 * ```
 * Вид · Размер · Подложка · Цвета · Строки · После · Платформа   ← оглавление от 768
 * ┌ Вид корзины ─────────── панель справа ⌃┐
 * ┌ Размер ──────────────── 300 / 448 px  ⌃┐
 * ┌ Подложка ─────────────────── 70 %     ⌃┐
 * ┌ Цвета ────────────── тёмный · авто    ⌃┐
 * ┌ Строки товара ─────── фото 80 · 8 px  ⌃┐
 * ┌ После «В корзину» ── открыть корзину  ⌃┐
 * └ Задаёт платформа ─── только просмотр  ⌄┘   ← свёрнута
 * ```
 * Без формы: группы читают общий контракт из контекста (`cart-look-context`)
 * и пишут разделом. Предупреждения `cartLookWarnings` — «!» у группы и
 * строкой под полем, не запрет. Без права — строка «Только просмотр…», поля
 * неактивны, запись не уходит.
 */

import { cartLookWarnings, type SiteCart, type SiteTheme } from "@chaiberi/shared";
import { useMemo } from "react";

import { NoticeStrip } from "../../ui/NoticeStrip";
import { SettingsGroups } from "../../ui/settings-groups";
import { CartLookContext, type CartLookApi } from "./cart-look-context";
import { CartAfterAddGroup } from "./CartAfterAddGroup";
import { CartColorsGroup } from "./CartColorsGroup";
import { CartModeGroup } from "./CartModeGroup";
import { CartOverlayGroup } from "./CartOverlayGroup";
import { CartPlatformGroup } from "./CartPlatformGroup";
import { CartRowsGroup } from "./CartRowsGroup";
import { CartSizeGroup } from "./CartSizeGroup";
import { CART_READONLY_TEXT } from "../../../lib/site-view/cart-look";

interface Props {
  cart: SiteCart;
  theme: SiteTheme;
  readOnly: boolean;
  onChange: (patch: Partial<SiteCart>) => void;
  onPreview?: (patch: Partial<SiteCart>) => void;
}

const noop = (): void => undefined;

export function CartLookFields({ cart, theme, readOnly, onChange, onPreview }: Props) {
  const api = useMemo<CartLookApi>(
    () => ({
      cart,
      theme,
      readOnly,
      warnings: cartLookWarnings(cart, theme),
      // Без права запись не уходит, даже если кнопку нажали в обход fieldset.
      change: readOnly ? noop : onChange,
      preview: readOnly || !onPreview ? noop : onPreview,
    }),
    [cart, theme, readOnly, onChange, onPreview],
  );
  return (
    <CartLookContext.Provider value={api}>
      <div className="flex min-w-0 flex-col gap-4">
        {readOnly ? <NoticeStrip>{CART_READONLY_TEXT}</NoticeStrip> : null}
        <SettingsGroups panel="cart" defaultCollapsed={["platform"]}>
          <CartModeGroup />
          <CartSizeGroup />
          <CartOverlayGroup />
          <CartColorsGroup />
          <CartRowsGroup />
          <CartAfterAddGroup />
          <CartPlatformGroup />
        </SettingsGroups>
      </div>
    </CartLookContext.Provider>
  );
}
